import { useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { Banner } from "../components/Banner";
import { Pill } from "../components/Pill";
import { api } from "../lib/api";
import "./SettingsPage.css";

interface Prefs {
  telegram_chat_id: string | null;
  telegram_linked_at: string | null;
  email_recipients: string[];
  digest_enabled: boolean;
  timezone: string;
  report_email_active: boolean;
  master_rollup_auto_send: boolean;
  report_ai_commentary: boolean;
}

interface AccountSetting {
  account_id: string;
  display_name: string;
  report_weekly_active: boolean;
  report_monthly_active: boolean;
}

interface SettingsResponse {
  client: { client_id: string };
  prefs: Prefs | null;
  accounts: AccountSetting[];
  telegramBotUsername: string | null;
}

interface TestResponse {
  attempted: number;
  sent: number;
  telegram: { sent: boolean; error?: string };
  email: { sent: boolean; recipients: number; error?: string };
}

type Toast = { tone: "ok" | "err"; text: string } | null;

export function SettingsPage() {
  const [data, setData] = useState<SettingsResponse | null>(null);
  const [linkCode, setLinkCode] = useState<string | null>(null);
  const [emailDraft, setEmailDraft] = useState("");
  const [toast, setToast] = useState<Toast>(null);
  const [busy, setBusy] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);
  const [metaConnected, setMetaConnected] = useState<boolean | null>(null);
  const [tokenDraft, setTokenDraft] = useState("");

  async function refresh() {
    const next = await api<SettingsResponse>("/api/settings");
    setData(next);
    setEmailDraft((next.prefs?.email_recipients ?? []).join("\n"));
    const me = await api<{ meta: { connected: boolean } | null }>("/api/me");
    setMetaConnected(me.meta?.connected ?? false);
  }

  async function patchReportSettings(patch: {
    accounts?: { account_id: string; report_weekly_active?: boolean; report_monthly_active?: boolean }[];
    master_rollup_auto_send?: boolean;
    report_ai_commentary?: boolean;
  }) {
    setReportBusy(true);
    try {
      await api("/api/settings", {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
      });
      await refresh();
    } catch (e) {
      setToast({ tone: "err", text: (e as Error).message });
    } finally {
      setReportBusy(false);
    }
  }

  function handleAccountToggle(
    accountId: string,
    field: "report_weekly_active" | "report_monthly_active",
    value: boolean,
  ) {
    void patchReportSettings({ accounts: [{ account_id: accountId, [field]: value }] });
  }

  function handleMasterRollupToggle(value: boolean) {
    void patchReportSettings({ master_rollup_auto_send: value });
  }

  function handleAiCommentaryToggle(value: boolean) {
    void patchReportSettings({ report_ai_commentary: value });
  }

  useEffect(() => {
    refresh().catch((e) => setToast({ tone: "err", text: e.message }));
  }, []);

  async function connectTelegram() {
    setBusy(true);
    try {
      const r = await api<{ code: string }>("/api/settings/telegram/link", { method: "POST" });
      setLinkCode(r.code);
    } catch (e) {
      setToast({ tone: "err", text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function disconnectTelegram() {
    setBusy(true);
    try {
      await api("/api/settings/telegram/unlink", { method: "POST" });
      setLinkCode(null);
      await refresh();
      setToast({ tone: "ok", text: "Telegram disconnected." });
    } catch (e) {
      setToast({ tone: "err", text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function disconnectMeta() {
    if (
      !window.confirm(
        "Disconnect your Meta (Facebook) account?\n\n" +
          "This permanently deletes all imported ad data, insights, alerts, reallocation " +
          "recommendations, and generated reports from Retrofit AI Monitor. Your login stays " +
          "active and you can reconnect anytime.",
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await api("/api/auth/disconnect", { method: "POST" });
      await refresh();
      setToast({ tone: "ok", text: "Meta account disconnected. All imported ad data was deleted." });
    } catch (e) {
      setToast({ tone: "err", text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function connectWithToken() {
    const token = tokenDraft.trim();
    if (!token) return;
    setBusy(true);
    try {
      const r = await api<{ ok: boolean; accounts: { id: string; name: string }[]; backfillDays: number }>("/api/auth/system-token", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token }),
      });
      setTokenDraft("");
      await refresh();
      const names = r.accounts.map((a) => a.name).join(", ");
      setToast({ tone: "ok", text: `Connected ${r.accounts.length} ad account${r.accounts.length === 1 ? "" : "s"} (${names}). Pulling the last ${r.backfillDays} days of history in the background.` });
    } catch (e) {
      setToast({ tone: "err", text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function saveEmail() {
    setBusy(true);
    try {
      const recipients = emailDraft
        .split(/[\n,;]+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      const r = await api<{ recipients: string[] }>("/api/settings/email", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ recipients }),
      });
      setEmailDraft(r.recipients.join("\n"));
      await refresh();
      setToast({ tone: "ok", text: `Saved ${r.recipients.length} recipient${r.recipients.length === 1 ? "" : "s"}.` });
    } catch (e) {
      setToast({ tone: "err", text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function sendTest() {
    setBusy(true);
    try {
      const r = await api<TestResponse>("/api/settings/test", { method: "POST" });
      if (r.attempted === 0) {
        setToast({ tone: "err", text: "Connect Telegram or add an email recipient first." });
      } else if (r.sent === r.attempted) {
        setToast({ tone: "ok", text: "Test alert sent. Check Telegram and/or your inbox." });
      } else if (r.sent === 0) {
        setToast({
          tone: "err",
          text: `All channels failed. Telegram: ${r.telegram.error ?? "ok"}. Email: ${r.email.error ?? "ok"}.`,
        });
      } else {
        setToast({
          tone: "err",
          text: `Partial: ${r.sent}/${r.attempted} channel(s) delivered. ${r.email.error ?? r.telegram.error ?? ""}`,
        });
      }
    } catch (e) {
      setToast({ tone: "err", text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  const prefs = data?.prefs;
  const telegramConnected = !!prefs?.telegram_chat_id;
  const hasRecipients = (prefs?.email_recipients ?? []).length > 0;

  return (
    <Layout clientId={data?.client.client_id}>
      <div style={{ marginBottom: "var(--space-5)" }}>
        <span className="label">Notifications</span>
        <h1 style={{ marginTop: "6px" }}>Settings</h1>
        <p style={{ color: "var(--color-bone-dim)", margin: "8px 0 0" }}>
          Choose how you want to be alerted when an ad-performance rule fires.
        </p>
      </div>

      {toast && <Banner tone={toast.tone}>{toast.text}</Banner>}
      {data === null && !toast && <Banner tone="info">Loading…</Banner>}

      {data && (
        <>
          {/* Meta connection card */}
          <Card
            title="Meta connection"
            actions={
              metaConnected ? <Pill tone="ok">Connected</Pill> : <Pill tone="quiet">Not connected</Pill>
            }
          >
            {metaConnected ? (
              <>
                <p style={{ color: "var(--color-bone-dim)", margin: "0 0 16px" }}>
                  Your Facebook/Meta ad accounts are connected and being monitored. Disconnecting
                  permanently deletes all imported ad data, insights, alerts, reallocation
                  recommendations, and generated reports. Your login stays active and you can
                  reconnect anytime.
                </p>
                <Button variant="danger" onClick={disconnectMeta} disabled={busy}>
                  Disconnect Meta account
                </Button>
              </>
            ) : (
              <>
                <p style={{ color: "var(--color-bone-dim)", margin: "0 0 16px" }}>
                  No Meta account is connected. Connect one to start monitoring your ad performance.
                </p>
                <Button onClick={() => { window.location.href = "/meta/start"; }} disabled={busy}>
                  Connect Meta account
                </Button>
                <div style={{ marginTop: "24px", paddingTop: "20px", borderTop: "1px solid var(--color-ink-600)" }}>
                  <p style={{ color: "var(--color-bone-dim)", margin: "0 0 10px" }}>
                    <strong style={{ color: "var(--color-bone)" }}>Or connect with a system-user token.</strong> In Meta Business
                    Settings → Users → System users, assign the system user to this app (Develop app) and to your ad account, then
                    Generate token (ads_read, never expires) and paste it here. It is stored encrypted and never shown again.
                  </p>
                  <input
                    className="email-recipients"
                    style={{ minHeight: 0 }}
                    type="password"
                    autoComplete="off"
                    spellCheck={false}
                    value={tokenDraft}
                    onChange={(e) => setTokenDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") void connectWithToken(); }}
                    placeholder="EAA…"
                    aria-label="System user access token"
                  />
                  <div style={{ marginTop: "12px" }}>
                    <Button variant="secondary" onClick={connectWithToken} disabled={busy || tokenDraft.trim().length < 40}>
                      Connect with token
                    </Button>
                  </div>
                </div>
              </>
            )}
          </Card>

          {/* Telegram card */}
          <Card
            title="Telegram"
            actions={telegramConnected ? <Pill tone="ok">Connected</Pill> : <Pill tone="quiet">Not connected</Pill>}
          >
            {telegramConnected && !linkCode && (
              <>
                <p className="setting-row__meta">
                  Linked to chat <code>{prefs!.telegram_chat_id}</code>
                  {prefs!.telegram_linked_at && <> on {new Date(prefs!.telegram_linked_at).toLocaleString()}</>}.
                </p>
                <Button variant="danger" onClick={disconnectTelegram} disabled={busy}>
                  Disconnect
                </Button>
              </>
            )}

            {!telegramConnected && !linkCode && (
              <>
                <p style={{ color: "var(--color-bone-dim)", margin: "0 0 16px" }}>
                  Receive critical ad alerts as instant push notifications on your phone.
                </p>
                <Button onClick={connectTelegram} disabled={busy}>
                  Connect Telegram
                </Button>
              </>
            )}

            {linkCode && (
              <div className="link-flow">
                <p>1. Open Telegram and start a chat with the bot:</p>
                {data.telegramBotUsername ? (
                  <p>
                    <a
                      className="link-flow__open"
                      href={`https://t.me/${data.telegramBotUsername}?start=${linkCode}`}
                      target="_blank"
                      rel="noopener"
                    >
                      Open {data.telegramBotUsername} ↗
                    </a>
                  </p>
                ) : (
                  <p style={{ color: "var(--color-bone-dim)" }}>
                    Bot username not configured server-side. Paste the code to the bot manually.
                  </p>
                )}
                <p>2. Or send this code to the bot as your first message:</p>
                <p className="link-flow__code">{linkCode}</p>
                <p style={{ color: "var(--color-bone-dim)", fontSize: "var(--fs-sm)" }}>
                  This code expires in 15 minutes. After Telegram replies "Connected," refresh this page.
                </p>
                <Button variant="ghost" onClick={refresh} disabled={busy}>
                  Refresh
                </Button>
              </div>
            )}
          </Card>

          {/* Email card */}
          <Card
            title="Email"
            actions={
              hasRecipients ? (
                <Pill tone="ok">
                  {prefs!.email_recipients.length} recipient{prefs!.email_recipients.length === 1 ? "" : "s"}
                </Pill>
              ) : (
                <Pill tone="quiet">No recipients</Pill>
              )
            }
          >
            <p style={{ color: "var(--color-bone-dim)", margin: "0 0 12px" }}>
              One email per line. Used for daily digests, weekly summaries, and (until Telegram is connected) any
              critical alerts that fire.
            </p>
            <textarea
              className="email-recipients"
              value={emailDraft}
              onChange={(e) => setEmailDraft(e.target.value)}
              placeholder="alice@yourcompany.com&#10;bob@yourcompany.com"
              rows={4}
            />
            <div style={{ marginTop: "16px" }}>
              <Button onClick={saveEmail} disabled={busy}>
                Save recipients
              </Button>
            </div>
          </Card>

          {/* Test card */}
          {(telegramConnected || hasRecipients) && (
            <Card title="Test a notification">
              <p style={{ color: "var(--color-bone-dim)", margin: "0 0 12px" }}>
                Sends a fake critical alert through every channel you have configured above. Useful to confirm wiring
                before real rules start firing.
              </p>
              <Button variant="secondary" onClick={sendTest} disabled={busy}>
                Send test alert
              </Button>
            </Card>
          )}

          {/* Reports card */}
          <Card title="Reports">
            <p style={{ color: "var(--color-bone-dim)", margin: "0 0 20px" }}>
              Control which accounts generate weekly and monthly PDF reports, and whether the master rollup is sent
              automatically.
            </p>

            {prefs?.report_email_active === false && (
              <Banner tone="warn">
                Report emails are paused due to a bounce or spam complaint. Contact support to re-enable delivery.
              </Banner>
            )}

            {data!.accounts.length > 0 && (
              <div style={{ marginBottom: "24px" }}>
                <table className="report-toggles-table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", paddingBottom: "8px", color: "var(--color-bone-dim)", fontWeight: 500, fontSize: "var(--fs-sm)" }}>
                        Account
                      </th>
                      <th style={{ textAlign: "center", paddingBottom: "8px", color: "var(--color-bone-dim)", fontWeight: 500, fontSize: "var(--fs-sm)", width: "80px" }}>
                        Weekly
                      </th>
                      <th style={{ textAlign: "center", paddingBottom: "8px", color: "var(--color-bone-dim)", fontWeight: 500, fontSize: "var(--fs-sm)", width: "80px" }}>
                        Monthly
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data!.accounts.map((acct) => (
                      <tr key={acct.account_id}>
                        <td style={{ paddingBottom: "10px", paddingRight: "16px", color: "var(--color-bone)" }}>
                          {acct.display_name}
                        </td>
                        <td style={{ textAlign: "center", paddingBottom: "10px" }}>
                          <input
                            type="checkbox"
                            checked={acct.report_weekly_active}
                            disabled={reportBusy}
                            onChange={(e) =>
                              handleAccountToggle(acct.account_id, "report_weekly_active", e.target.checked)
                            }
                          />
                        </td>
                        <td style={{ textAlign: "center", paddingBottom: "10px" }}>
                          <input
                            type="checkbox"
                            checked={acct.report_monthly_active}
                            disabled={reportBusy}
                            onChange={(e) =>
                              handleAccountToggle(acct.account_id, "report_monthly_active", e.target.checked)
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {data!.accounts.length === 0 && (
              <p style={{ color: "var(--color-bone-dim)", margin: "0 0 16px", fontStyle: "italic" }}>
                No active ad accounts connected yet.
              </p>
            )}

            <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={prefs?.master_rollup_auto_send ?? true}
                disabled={reportBusy}
                onChange={(e) => handleMasterRollupToggle(e.target.checked)}
              />
              <span style={{ color: "var(--color-bone)" }}>
                Master rollup auto-send{" "}
                <span style={{ color: "var(--color-bone-dim)", fontSize: "var(--fs-sm)" }}>
                  (Mondays + 1st of month, 07:00 client-local)
                </span>
              </span>
            </label>

            <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", marginTop: 12 }}>
              <input
                type="checkbox"
                checked={prefs?.report_ai_commentary ?? true}
                disabled={reportBusy}
                onChange={(e) => handleAiCommentaryToggle(e.target.checked)}
              />
              <span style={{ color: "var(--color-bone)" }}>
                AI-drafted commentary{" "}
                <span style={{ color: "var(--color-bone-dim)", fontSize: "var(--fs-sm)" }}>
                  (pre-fill the report's commentary field with an AI-generated 3–5-sentence summary; you always edit and approve before sending)
                </span>
              </span>
            </label>
          </Card>
        </>
      )}
    </Layout>
  );
}
